// src/store/selectors/orderKarigarSelectors.js
// ============================================================================
// ORDER-KARIGAR INTEGRATION SELECTORS
// ============================================================================
// Selectors for accessing order-karigar state
//
// STATE PATH: state.karigar.order (via rootReducer)
// ============================================================================

import { createSelector } from "@reduxjs/toolkit";

// ============================================================================
// BASE SELECTORS
// ============================================================================

// State is stored at: state.karigar.order (from rootReducer)
const selectOrderKarigarState = (state) => state.karigar?.order || {};

// ============================================================================
// AVAILABLE KARIGARS SELECTORS
// ============================================================================

/**
 * Select available karigars data
 */
export const selectAvailableKarigars = createSelector(
  [selectOrderKarigarState],
  (orderKarigar) => orderKarigar.availableKarigars || {}
);

/**
 * Select product karigars (linked to specific product)
 * These are shown first and prioritized
 */
export const selectProductKarigars = createSelector(
  [selectAvailableKarigars],
  (available) => available.productKarigars || []
);

/**
 * Select other karigars (capable but not linked to product)
 */
export const selectOtherKarigars = createSelector(
  [selectAvailableKarigars],
  (available) => available.otherKarigars || []
);

/**
 * Select all available karigars (combined)
 */
export const selectAllAvailableKarigars = createSelector(
  [selectAvailableKarigars],
  (available) => available.allKarigars || []
);

/**
 * Select karigar recommendations
 */
export const selectKarigarRecommendations = createSelector(
  [selectAvailableKarigars],
  (available) => available.recommendations || {}
);

/**
 * Select availability summary
 */
export const selectAvailabilitySummary = createSelector(
  [selectAvailableKarigars],
  (available) => available.summary || null
);

/**
 * Select unavailable karigars (for reference)
 */
export const selectUnavailableKarigars = createSelector(
  [selectAvailableKarigars],
  (available) => available.unavailable || {}
);

/**
 * Select query info (what was searched)
 */
export const selectAvailabilityQuery = createSelector(
  [selectAvailableKarigars],
  (available) => available.query || null
);

// ============================================================================
// ORDER STATUS SELECTORS
// ============================================================================

/**
 * Select order karigar status
 */
export const selectOrderKarigarStatus = createSelector(
  [selectOrderKarigarState],
  (orderKarigar) => orderKarigar.orderStatus || {}
);

/**
 * Select if order has karigar work
 */
export const selectHasKarigarWork = createSelector(
  [selectOrderKarigarStatus],
  (status) => status.hasKarigarWork || false
);

/**
 * Select jobs for order
 */
export const selectOrderJobs = createSelector(
  [selectOrderKarigarStatus],
  (status) => status.jobs || []
);

/**
 * Select order karigar summary
 */
export const selectOrderKarigarSummary = createSelector(
  [selectOrderKarigarStatus],
  (status) => status.summary || null
);

/**
 * Select if order can be fulfilled
 */
export const selectCanFulfillOrder = createSelector(
  [selectOrderKarigarStatus],
  (status) => status.canFulfillOrder || false
);

/**
 * Select jobs by status
 */
export const selectOrderJobsByStatus = createSelector(
  [selectOrderJobs],
  (jobs) => {
    const byStatus = {};
    jobs.forEach((job) => {
      const status = job.status || "unknown";
      if (!byStatus[status]) byStatus[status] = [];
      byStatus[status].push(job);
    });
    return byStatus;
  }
);

/**
 * Select pending jobs
 */
export const selectPendingJobs = createSelector(
  [selectOrderJobsByStatus],
  (byStatus) => [
    ...(byStatus.assigned || []),
    ...(byStatus.accepted || []),
    ...(byStatus.in_progress || []),
    ...(byStatus.quality_check || []),
  ]
);

/**
 * Select completed jobs
 */
export const selectCompletedJobs = createSelector(
  [selectOrderJobsByStatus],
  (byStatus) => [...(byStatus.completed || []), ...(byStatus.delivered || [])]
);

/**
 * Select overdue jobs
 */
export const selectOverdueJobs = createSelector([selectOrderJobs], (jobs) =>
  jobs.filter((job) => job.isOverdue)
);

// ============================================================================
// KARIGAR QUEUE SELECTORS
// ============================================================================

/**
 * Select karigar queue state
 */
export const selectKarigarQueueState = createSelector(
  [selectOrderKarigarState],
  (orderKarigar) => orderKarigar.karigarQueue || {}
);

/**
 * Select karigar info from queue
 */
export const selectQueueKarigar = createSelector(
  [selectKarigarQueueState],
  (queue) => queue.karigar || null
);

/**
 * Select karigar capacity from queue
 */
export const selectQueueCapacity = createSelector(
  [selectKarigarQueueState],
  (queue) => queue.capacity || null
);

/**
 * Select queue jobs grouped by status
 */
export const selectQueueJobs = createSelector(
  [selectKarigarQueueState],
  (queue) => queue.queue || {}
);

/**
 * Select queue total jobs count
 */
export const selectQueueTotalJobs = createSelector(
  [selectKarigarQueueState],
  (queue) => queue.totalJobs || 0
);

/**
 * Select queue overdue jobs count
 */
export const selectQueueOverdueJobs = createSelector(
  [selectKarigarQueueState],
  (queue) => queue.overdueJobs || 0
);

/**
 * Select flattened queue jobs list
 */
export const selectQueueJobsList = createSelector(
  [selectQueueJobs],
  (queue) => {
    const jobs = [];
    Object.values(queue).forEach((statusJobs) => {
      if (Array.isArray(statusJobs)) {
        jobs.push(...statusJobs);
      }
    });
    return jobs;
  }
);

// ============================================================================
// CAN FULFILL CHECK SELECTORS
// ============================================================================

/**
 * Select can fulfill check result
 */
export const selectCanFulfillCheck = createSelector(
  [selectOrderKarigarState],
  (orderKarigar) => orderKarigar.canFulfillCheck || null
);

// ============================================================================
// ASSIGNMENT RESULT SELECTORS
// ============================================================================

/**
 * Select last assignment result
 */
export const selectLastAssignment = createSelector(
  [selectOrderKarigarState],
  (orderKarigar) => orderKarigar.lastAssignment || null
);

/**
 * Select last reassignment result
 */
export const selectLastReassignment = createSelector(
  [selectOrderKarigarState],
  (orderKarigar) => orderKarigar.lastReassignment || null
);

/**
 * Select bulk assignment results
 */
export const selectBulkAssignmentResults = createSelector(
  [selectOrderKarigarState],
  (orderKarigar) => orderKarigar.bulkAssignmentResults || null
);

// ============================================================================
// LOADING SELECTORS
// ============================================================================

/**
 * Select all loading states
 */
export const selectOrderKarigarLoading = createSelector(
  [selectOrderKarigarState],
  (orderKarigar) => orderKarigar.loading || {}
);

/**
 * Select specific loading states
 */
export const selectIsLoadingAvailableKarigars = createSelector(
  [selectOrderKarigarLoading],
  (loading) => loading.availableKarigars || false
);

export const selectIsLoadingBulkAvailability = createSelector(
  [selectOrderKarigarLoading],
  (loading) => loading.bulkAvailability || false
);

export const selectIsLoadingOrderStatus = createSelector(
  [selectOrderKarigarLoading],
  (loading) => loading.orderStatus || false
);

export const selectIsLoadingKarigarQueue = createSelector(
  [selectOrderKarigarLoading],
  (loading) => loading.karigarQueue || false
);

export const selectIsLoadingAssign = createSelector(
  [selectOrderKarigarLoading],
  (loading) => loading.assign || false
);

export const selectIsLoadingReassign = createSelector(
  [selectOrderKarigarLoading],
  (loading) => loading.reassign || false
);

export const selectIsLoadingBulkAssign = createSelector(
  [selectOrderKarigarLoading],
  (loading) => loading.bulkAssign || false
);

/**
 * Select if any operation is loading
 */
export const selectIsAnyLoading = createSelector(
  [selectOrderKarigarLoading],
  (loading) => Object.values(loading).some(Boolean)
);

// ============================================================================
// ERROR & SUCCESS SELECTORS
// ============================================================================

/**
 * Select error
 */
export const selectOrderKarigarError = createSelector(
  [selectOrderKarigarState],
  (orderKarigar) => orderKarigar.error || null
);

/**
 * Select success message
 */
export const selectOrderKarigarSuccessMessage = createSelector(
  [selectOrderKarigarState],
  (orderKarigar) => orderKarigar.successMessage || null
);

// ============================================================================
// COMPOSITE SELECTORS
// ============================================================================

/**
 * Select availability with loading state
 */
export const selectAvailabilityWithStatus = createSelector(
  [
    selectAvailableKarigars,
    selectIsLoadingAvailableKarigars,
    selectIsLoadingBulkAvailability,
    selectOrderKarigarError,
  ],
  (available, loadingAvailable, loadingBulk, error) => ({
    ...available,
    isLoading: loadingAvailable || loadingBulk,
    error,
  })
);

/**
 * Select order status with loading state
 */
export const selectOrderStatusWithLoading = createSelector(
  [
    selectOrderKarigarStatus,
    selectIsLoadingOrderStatus,
    selectOrderKarigarError,
  ],
  (status, isLoading, error) => ({
    ...status,
    isLoading,
    error,
  })
);

/**
 * Select karigar queue with loading state
 */
export const selectKarigarQueueWithLoading = createSelector(
  [
    selectKarigarQueueState,
    selectIsLoadingKarigarQueue,
    selectOrderKarigarError,
  ],
  (queue, isLoading, error) => ({
    ...queue,
    isLoading,
    error,
  })
);

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  // Available karigars
  selectAvailableKarigars,
  selectProductKarigars,
  selectOtherKarigars,
  selectAllAvailableKarigars,
  selectKarigarRecommendations,
  selectAvailabilitySummary,
  selectUnavailableKarigars,
  selectAvailabilityQuery,

  // Order status
  selectOrderKarigarStatus,
  selectHasKarigarWork,
  selectOrderJobs,
  selectOrderKarigarSummary,
  selectCanFulfillOrder,
  selectOrderJobsByStatus,
  selectPendingJobs,
  selectCompletedJobs,
  selectOverdueJobs,

  // Karigar queue
  selectKarigarQueueState,
  selectQueueKarigar,
  selectQueueCapacity,
  selectQueueJobs,
  selectQueueTotalJobs,
  selectQueueOverdueJobs,
  selectQueueJobsList,

  // Can fulfill
  selectCanFulfillCheck,

  // Assignment results
  selectLastAssignment,
  selectLastReassignment,
  selectBulkAssignmentResults,

  // Loading
  selectOrderKarigarLoading,
  selectIsLoadingAvailableKarigars,
  selectIsLoadingBulkAvailability,
  selectIsLoadingOrderStatus,
  selectIsLoadingKarigarQueue,
  selectIsLoadingAssign,
  selectIsLoadingReassign,
  selectIsLoadingBulkAssign,
  selectIsAnyLoading,

  // Error & success
  selectOrderKarigarError,
  selectOrderKarigarSuccessMessage,

  // Composite
  selectAvailabilityWithStatus,
  selectOrderStatusWithLoading,
  selectKarigarQueueWithLoading,
};
